import { ApiError } from './api'

/**
 * Shared by every form control, so a focus ring or error border is the same everywhere.
 *
 * Full width unless the control states its own: Tailwind emits `w-full` after `w-20`,
 * so a width passed alongside it was silently ignored — a quantity box in a table cell
 * then shrank with the cell until its number could not be read.
 */
export function controlClass(hasError = false, className = ''): string {
  return [
    /(?:^|\s)w-/.test(className) ? '' : 'w-full',
    // 16px on a phone: iOS zooms the whole page into any field smaller than that.
    'rounded-md border bg-dpl-surface px-3 py-2 text-base text-dpl-text sm:text-sm',
    'placeholder:text-dpl-muted/70 focus:outline-none focus:ring-1 disabled:bg-dpl-bg disabled:text-dpl-muted',
    hasError
      ? 'border-dpl-danger focus:border-dpl-danger focus:ring-dpl-danger'
      : 'border-dpl-border focus:border-dpl-primary focus:ring-dpl-primary',
  ]
    .filter(Boolean)
    .join(' ')
}

/**
 * The server's own words for why something failed.
 *
 * A refusal from the domain arrives as a 422 whose detail was written for the
 * person at the screen — "below this product's minimum of 15% — the lowest price
 * that meets it is KSh 117.65". Replacing that with "Something went wrong" throws
 * away the only useful part.
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return 'You do not have permission to do that.'
    if (error.status === 404) return 'That record no longer exists.'
    if (error.status === 429) return 'Too many requests in a short time. Wait a moment and try again.'
    return error.detail ?? error.title
  }

  return error instanceof Error ? error.message : 'Something went wrong.'
}

/** A number input's value, or null when blank — never NaN, never zero for "not set". */
export function numberOrNull(value: unknown): number | null {
  if (value === '' || value === null || value === undefined) return null
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}
